// What a document shows for its number, on the web.
//
// Nobody types a number (ADR-026). A document takes the next one when it goes
// out, so a draft holds none, and what it shows is the number it is EXPECTED
// to take: the company's, not the draft's, and the same for every draft of a
// kind. It moves whenever anything else in the company goes out, from this
// tab, another one, the phone or the cron, and no draft's row changes when it
// does. So it is never stored on a row or carried in a draft: it is the
// `numbering` block the server serves beside the lists, the sends and GET
// /document-numbers, kept here once and read wherever a number is drawn.
//
// The rules themselves are shared/document-number.js's, so the phone shows,
// orders and falls back exactly as this does.
import { api } from "./api.js";
import { state } from "./state.js";
import {
  MAX_SEQUENCE,
  NUMBER_PLACEHOLDER,
  LISTED_DRAFT,
  checkNextNumberSetting,
  expectedUnderPrefix,
  listedDocumentNumber,
  listedNumberSortText,
  nextNumberSentence,
  shownDocumentNumber
} from "../../../shared/document-number.js";

export { NUMBER_PLACEHOLDER };

/**
 * Keep the `numbering` a response carried, if it carried one. Returns the
 * block now held. `kind` keys are "invoice", "quote" and "creditNote".
 */
export function applyNumbering(data) {
  const numbering = data?.numbering;
  if (numbering && typeof numbering === "object") state.numbering = numbering;
  return state.numbering;
}

/** The number the next document of a kind is expected to take, or null. */
export function expectedNumber(kind) {
  return state.numbering?.[kind]?.expected || null;
}

/**
 * What a document shows ON ITSELF (a preview, an email, a PDF's name): the
 * number it holds, else the one it expects, else "[number]".
 */
export function shownNumber(kind, held) {
  return shownDocumentNumber(held, expectedNumber(kind), NUMBER_PLACEHOLDER);
}

/**
 * What a document's OWN title shows (its detail drawer): the number it holds,
 * else the one it expects, else "Draft". Only a draft is ever given the
 * expected number: one that went out always holds its own.
 */
export function titledNumber(kind, held, status = "draft") {
  if (String(status || "draft") !== "draft") return String(held || "").trim();
  return shownDocumentNumber(held, expectedNumber(kind), "Draft");
}

/**
 * What a document is called in a LIST, its search and a row's label: the
 * number it holds, else "Draft", never the number a draft expects, which every
 * draft of a kind shares (#892, shared/document-number.js).
 */
export function listedNumber(held, status = "draft") {
  return listedDocumentNumber(held, status);
}

/**
 * The words a list row's button is read out as: "View invoice INV-0457", or
 * "View draft invoice for Hartley & Sons" for a draft with no number of its
 * own, since "View invoice Draft" names nothing.
 */
export function listedRowLabel(noun, listed, client) {
  if (listed && listed !== LISTED_DRAFT) return `View ${noun} ${listed}`;
  const name = String(client || "").trim();
  return name ? `View draft ${noun} for ${name}` : `View draft ${noun}`;
}

/** The one value a list sorts a row by number by (shared/document-number.js). */
export function listedSortValue(held, status = "draft") {
  return listedNumberSortText(held, status);
}

/**
 * Ask for the numbering afresh, for a screen that is about to show a draft's
 * number: compose, when it opens and whenever the tab comes back to the front.
 * A failure keeps what was held, and falls back to "[number]" with nothing.
 */
export async function refreshNumbering() {
  try {
    applyNumbering(await api("/document-numbers"));
  } catch {
    // Keep what was held. A stale number is corrected by the send, which says
    // the number it actually gave; a made-up one would never be.
  }
  return state.numbering;
}

/**
 * What an edit screen holds after a send that failed. A send fixes the number
 * before it builds or emails anything (server/document-number.js), so one
 * that fails after that (the email refused, the PDF failed) leaves the draft
 * HOLDING the number it will go out under, with its version moved by the
 * send's own writes. The screen opened on the number the draft held then,
 * usually none, and went on showing the expected one, which is by now the
 * NEXT number: the preview, the email and the attachment's name would all say
 * INV-0006 while the retry sends INV-0005, and the retry's save would be
 * refused as changed elsewhere by nothing but its own send.
 *
 * So the screen reads the draft again. `read` answers the draft as the API
 * serves it (or throws); `numberKey` is "invoiceNumber" or "quoteNumber";
 * `held` and `version` are what the screen has; `savedAt` is the `updatedAt`
 * its own last save answered with. Answers { held, version }: the number the
 * draft holds now, and the version to base the next save on. That is the one
 * just read only when nobody has saved the draft since this screen did: a
 * save stamps `updatedAt`, and a send's claim, number and release don't, so
 * an edit made elsewhere in between is still put to the person rather than
 * saved over. A read that fails, or a draft that has gone out or gone, leaves
 * both as they were; the save that follows says what happened.
 */
export async function draftAfterFailedSend({ read, numberKey, held = null, version, savedAt = null } = {}) {
  let latest = null;
  try {
    latest = await read();
  } catch {
    return { held, version };
  }
  if (!latest || String(latest.status || "").toLowerCase() !== "draft") return { held, version };
  const holds = String(latest[numberKey] ?? "").trim() || held;
  const onlyTheSend = savedAt != null && latest.updatedAt === savedAt && Number.isInteger(latest.version);
  return { held: holds, version: onlyTheSend ? latest.version : version };
}

/**
 * Keep a compose screen's number fresh while it is open: whenever the tab
 * comes back to the front, ask again, and redraw if it moved. `node` is an
 * element of the screen: once it has left the page the screen has gone, and
 * the listener goes with it rather than redrawing a form nobody can see.
 */
export function refreshNumberingOnReturn(node, redraw) {
  const onVisible = async () => {
    if (!node?.isConnected) {
      document.removeEventListener("visibilitychange", onVisible);
      return;
    }
    if (document.visibilityState !== "visible") return;
    const before = JSON.stringify(state.numbering ?? null);
    await refreshNumbering();
    if (node.isConnected && JSON.stringify(state.numbering ?? null) !== before) redraw();
  };
  document.addEventListener("visibilitychange", onVisible);
}

// ─── The next-number setting (Documents › Numbering) ────────────────────────
// Where each kind's numbering picks up, for an owner carrying on from another
// app: forward only, above the last number Sendinvo gave out. The rule and its
// sentences are shared/document-number.js's checkNextNumberSetting, which the
// server checks the save against too, so the field and the refusal can't
// disagree. The floor is the `numbering` the server last served, which is why
// the settings screen asks for it afresh when it opens: anything sent from
// another tab, the phone or the cron since moves it.

/** The field each kind's setting is saved as (POST /company/details). */
export const NEXT_NUMBER_FIELDS = { invoice: "invoiceNextNumber", quote: "quoteNextNumber", creditNote: "creditNoteNextNumber", paymentSchedule: "paymentScheduleNextNumber" };

/**
 * What the next-number field says about what's typed: { ok, sequence, hint,
 * refusal }, the hint being "Your next invoice will be INV-0500." Before the
 * numbering is known there is no floor to check against, so only the shape of
 * the number is checked and nothing is promised: the server has the last word.
 */
export function checkNextNumber(kind, typed, prefix) {
  const numbering = state.numbering?.[kind];
  const checked = checkNextNumberSetting({
    kind,
    // A prefix being typed beside it (Numbering's drawer, 15 October 2026)
    // is the one the number is said under.
    prefix: prefix ?? numbering?.prefix ?? "",
    lastSequence: numbering?.lastSequence ?? 0,
    lastNumber: numbering?.lastNumber ?? null,
    typed
  });
  return numbering ? checked : { ...checked, hint: null };
}

/**
 * The number a prefix field says the next document will take, as the prefix is
 * typed: "Your next invoice will be SP-0458.", or null before the numbering is
 * known. The count carries across a prefix change, but a prefix the company
 * used before may have numbers of its own ahead of it, so this is the server's
 * own rule under the typed prefix (shared/document-number.js,
 * expectedUnderPrefix), and the number the save will then show.
 */
export function nextNumberUnderPrefix(kind, prefix) {
  const choice = expectedUnderPrefix(state.numbering?.[kind], prefix);
  return choice ? nextNumberSentence(kind, choice.number) : null;
}

/**
 * Whether the setting can take any number at all. Stored numbers are read to
 * fifteen digits, so a company that brought a longer scheme over from another
 * app (INV-2025090001) carries on from it; the setting stops at nine, so once
 * numbering is past that there is nothing above the last number it could be
 * set to, and a field no value can satisfy isn't offered.
 */
export function nextNumberSettable(kind) {
  const last = Number(state.numbering?.[kind]?.lastSequence);
  return !(Number.isFinite(last) && last >= MAX_SEQUENCE);
}

/**
 * What pressing Update does with the next-number field: nothing, when it still
 * holds what it opened on, since nothing would be sent (and what it opened on
 * is the number Sendinvo filled in itself, so it is never refused); otherwise
 * the field's own check. { unchanged: true }, or checkNextNumber's answer.
 */
export function nextNumberOnUpdate(kind, typed, opened) {
  if (String(typed ?? "").trim() === String(opened ?? "").trim()) return { unchanged: true };
  return checkNextNumber(kind, typed);
}

/**
 * Where a failed next-number save's words go: { field } for the server's
 * refusal of the number itself (NextNumberRefused, something went out since
 * the field opened), shown under the field; { drawer } for anything else,
 * which isn't the number's fault and mustn't read as if it were.
 */
export function nextNumberSaveFailure(err) {
  if (err?.status === 400 && err?.body?.error === "NextNumberRefused") return { field: err.message };
  return { drawer: err?.message || "Couldn\u2019t save your changes. Please try again." };
}
